import { FC, useEffect, useState } from 'react'
import { Box, Typography } from '@mui/material'
import { EstadoDeLista } from '@/components/common/EstadoDeLista'
import { TituloSecao } from '@/components/dashboard/TituloSecao'
import { dashboardService } from '@/services/dashboardService'
import { FaixaRanking, RankingJanela, RankingProprio } from '@/types/dashboard'
import { tokens } from '@/theme'

type Estado = 'carregando' | 'pronto' | 'oculto' | 'erro'

/** Faixa percentual calculada no servidor; DEMAIS não ganha complemento (nem posição). */
const TEXTO_FAIXA: Record<FaixaRanking, string | null> = {
  TOP_10: 'entre os 10% primeiros',
  TOP_25: 'entre os 25% primeiros',
  TOP_50: 'entre os 50% primeiros',
  DEMAIS: null,
}

const linha = (j: RankingJanela, sufixo: string) => {
  // Universo pequeno: o backend não manda posição nem faixa (Q28).
  if (!j.disponivel) return `Ranking indisponível no período (${j.rotuloPeriodo})`
  // Metade de baixo: sem posição (o último colocado deduziria o tamanho da base).
  if (j.faixa === 'DEMAIS') return `Continue avançando ${sufixo} — por volume ganho no período`
  if (j.posicao == null) return `Sem volume ganho em ${j.rotuloPeriodo}`
  const faixa = j.faixa ? TEXTO_FAIXA[j.faixa] : null
  return `${j.posicao}º lugar ${sufixo}${faixa ? ` · ${faixa}` : ''}`
}

/**
 * Posição do próprio consultor (VC-04, AN-5 §3.2/§4; Q28): "3º lugar no mês · entre os 10%
 * primeiros", por volume ganho entre consultores com movimento no período. Q28: o consultor
 * recebe posição e faixa percentual — nunca nomes, valores de terceiros nem o tamanho da base
 * (o backend nem manda). 403 (não é consultor, ex.: ONBOARDING) ou 404
 * (backend sem a rota) → o card some; outra falha → "Sem dados no momento".
 *
 * `onOcultar` avisa a página para liberar a coluna.
 */
export const CardRankingProprio: FC<{ onOcultar?: () => void }> = ({ onOcultar }) => {
  const [estado, setEstado] = useState<Estado>('carregando')
  const [ranking, setRanking] = useState<RankingProprio | null>(null)

  useEffect(() => {
    let ativo = true
    dashboardService
      .getRankingProprio()
      .then((r) => {
        if (!ativo) return
        setRanking(r)
        setEstado('pronto')
      })
      .catch((e: unknown) => {
        if (!ativo) return
        const status = (e as { status?: number })?.status
        if (status === 403 || status === 404) {
          setEstado('oculto')
          onOcultar?.()
        } else {
          setEstado('erro')
        }
      })
    return () => {
      ativo = false
    }
    // onOcultar é callback da página; recarregar por causa dele não faz sentido.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  if (estado === 'oculto') return null

  return (
    <Box data-testid="card-ranking-proprio">
      <TituloSecao titulo="Seu ranking" />
      {estado === 'carregando' ? (
        <EstadoDeLista estado="carregando" minHeight={160} />
      ) : estado === 'erro' || !ranking ? (
        <EstadoDeLista estado="vazio" mensagem="Sem dados no momento" minHeight={160} />
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          <Typography sx={{ fontWeight: 700, color: tokens.brandInk, fontSize: '1.125rem' }}>
            {linha(ranking.mensal, 'no mês')}
          </Typography>
          <Typography sx={{ fontWeight: 700, color: tokens.brandInk, fontSize: '1.125rem' }}>
            {linha(ranking.anual, 'no ano')}
          </Typography>
          <Typography variant="caption" sx={{ color: tokens.textSecondary }}>
            {/* AN-8 §5: o escopo vem pronto do backend ("na sua equipe"…); sem ele, a legenda antiga. */}
            {ranking.escopoRotulo
              ? `Por volume ganho · ${ranking.escopoRotulo}`
              : 'Por volume ganho no período'}
          </Typography>
        </Box>
      )}
    </Box>
  )
}

export default CardRankingProprio
